import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import Papa from "papaparse";
import { API } from "aws-amplify";
import { createLLMModel, updateLLMModel } from "../../graphql/mutations";
import { listLLMModels } from "../../graphql/queries";
import { CreateLLMModelInput, UpdateLLMModelInput } from "../../API";

interface CSVModel {
  id: string;
  status: string;
  vendor: string;
  host: string;
  supported: string; // CSV will have "true"/"false" strings
  name: string;
  description?: string;
  modelKey?: string;
  modelKeyLatest?: string;
  inputTokenPrice?: string;
  cachedTokenPrice?: string;
  outputTokenPrice?: string;
  inputTokenBatchPrice?: string;
  outputTokenBatchPrice?: string;
  searchCost?: string;
  textInputSupport: string;
  imageInputSupport: string;
  audioInputSupport: string;
  textOutputSupport: string;
  imageOutputSupport: string;
  audioOutputSupport: string;
  thinkingSupport: string;
  contextWindowTokens?: string;
  maxResponseTokens?: string;
  knowledgeCutoffDate?: string;
  lastCheckedDate?: string;
  supportedLanguages?: string;
}

interface ModelRecord {
  id: string;
  status: string;
  vendor: string;
  host: string;
  supported: boolean;
  name: string;
  description?: string | null;
  modelKey?: string | null;
  modelKeyLatest?: string | null;
  inputTokenPrice?: number | null;
  cachedTokenPrice?: number | null;
  outputTokenPrice?: number | null;
  inputTokenBatchPrice?: number | null;
  outputTokenBatchPrice?: number | null;
  searchCost?: number | null;
  textInputSupport: boolean;
  imageInputSupport: boolean;
  audioInputSupport: boolean;
  textOutputSupport: boolean;
  imageOutputSupport: boolean;
  audioOutputSupport: boolean;
  thinkingSupport: boolean;
  contextWindowTokens?: number | null;
  maxResponseTokens?: number | null;
  knowledgeCutoffDate?: string | null;
  lastCheckedDate?: string | null;
  supportedLanguages?: string | null;
}

export default function AdminModels() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [models, setModels] = useState<ModelRecord[]>([]);
  const [selectedModel, setSelectedModel] = useState<ModelRecord | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [filterVendor, setFilterVendor] = useState("");

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const result = (await API.graphql({ query: listLLMModels })) as {
        data: {
          listLLMModels: {
            items: ModelRecord[];
          };
        };
      };
      setModels(result.data.listLLMModels.items);
    } catch (err) {
      setError("Error fetching models: " + String(err));
    }
    setLoading(false);
  };

  const handleEditClick = (model: ModelRecord) => {
    setSelectedModel(model);
    setOpenEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!selectedModel) {
      console.log("No selected model");
      return;
    }

    console.log("Saving model:", selectedModel);

    try {
      console.log("Creating update input:", selectedModel);
      const updateLLMModelInput: UpdateLLMModelInput = {
        id: selectedModel.id,
        status: selectedModel.status,
        vendor: selectedModel.vendor,
        host: selectedModel.host,
        supported: selectedModel.supported,
        name: selectedModel.name,
        description: selectedModel.description || null,
        modelKey: selectedModel.modelKey || null,
        modelKeyLatest: selectedModel.modelKeyLatest || null,
        inputTokenPrice: selectedModel.inputTokenPrice
          ? selectedModel.inputTokenPrice
          : undefined,
        cachedTokenPrice: selectedModel.cachedTokenPrice
          ? selectedModel.cachedTokenPrice
          : undefined,
        outputTokenPrice: selectedModel.outputTokenPrice
          ? selectedModel.outputTokenPrice
          : undefined,
        inputTokenBatchPrice: selectedModel.inputTokenBatchPrice
          ? selectedModel.inputTokenBatchPrice
          : undefined,
        outputTokenBatchPrice: selectedModel.outputTokenBatchPrice
          ? selectedModel.outputTokenBatchPrice
          : undefined,
        searchCost: selectedModel.searchCost
          ? selectedModel.searchCost
          : undefined,
        textInputSupport: selectedModel.textInputSupport,
        imageInputSupport: selectedModel.imageInputSupport,
        audioInputSupport: selectedModel.audioInputSupport,
        textOutputSupport: selectedModel.textOutputSupport,
        imageOutputSupport: selectedModel.imageOutputSupport,
        audioOutputSupport: selectedModel.audioOutputSupport,
        thinkingSupport: selectedModel.thinkingSupport,
        contextWindowTokens: selectedModel.contextWindowTokens
          ? selectedModel.contextWindowTokens
          : undefined,
        maxResponseTokens: selectedModel.maxResponseTokens
          ? selectedModel.maxResponseTokens
          : undefined,
        knowledgeCutoffDate: selectedModel.knowledgeCutoffDate
          ? selectedModel.knowledgeCutoffDate
          : undefined,
        lastCheckedDate: selectedModel.lastCheckedDate
          ? selectedModel.lastCheckedDate
          : undefined,
        supportedLanguages: selectedModel.supportedLanguages
          ? selectedModel.supportedLanguages
          : undefined,
      };

      console.log("Update input:", updateLLMModelInput);
      console.log("Calling API.graphql with mutation:", updateLLMModel);

      try {
        const response = await API.graphql({
          query: updateLLMModel,
          variables: { input: updateLLMModelInput },
        });
        console.log("Raw API Response:", response);

        if ("errors" in response) {
          throw new Error(JSON.stringify(response.errors));
        }

        console.log("Update successful:", response);
        setSuccess("Model updated successfully");
        setOpenEditDialog(false);
        await fetchModels();
      } catch (graphqlError) {
        console.error("GraphQL Error:", graphqlError);
        throw graphqlError;
      }
    } catch (err) {
      console.error("Save Error:", err);
      setError("Error updating model: " + String(err));
    }
  };

  const columns: GridColDef[] = [
    { field: "vendor", headerName: "Vendor", width: 100 },
    { field: "host", headerName: "Host", width: 100 },
    { field: "name", headerName: "Name", width: 200 },
    {
      field: "supported",
      headerName: "Supported",
      width: 100,
      type: "boolean",
    },
    {
      field: "inputTokenPrice",
      headerName: "Input Price",
      width: 130,
      type: "number",
    },
    {
      field: "outputTokenPrice",
      headerName: "Output Price",
      width: 130,
      type: "number",
    },

    {
      field: "imageInputSupport",
      headerName: "Image In",
      width: 100,
      type: "boolean",
    },
    {
      field: "audioInputSupport",
      headerName: "Audio In",
      width: 100,
      type: "boolean",
    },
    {
      field: "textOutputSupport",
      headerName: "Text Out",
      width: 100,
      type: "boolean",
    },
    {
      field: "imageOutputSupport",
      headerName: "Image Out",
      width: 100,
      type: "boolean",
    },
    {
      field: "audioOutputSupport",
      headerName: "Audio Out",
      width: 100,
      type: "boolean",
    },
    {
      field: "thinkingSupport",
      headerName: "Thinking",
      width: 100,
      type: "boolean",
    },
    {
      field: "contextWindowTokens",
      headerName: "Context Tokens",
      width: 130,
      type: "number",
    },
    {
      field: "maxResponseTokens",
      headerName: "Max Tokens",
      width: 130,
      type: "number",
    },
    {
      field: "knowledgeCutoffDate",
      headerName: "Knowledge Cutoff",
      width: 150,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => handleEditClick(params.row)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setSuccess(null);
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      complete: handleParsedData,
      header: true,
      skipEmptyLines: true,
    });
  };

  const parseDollarAmount = (value: string | undefined): number | null => {
    if (!value) return null;
    // Remove '$' and any whitespace, then parse as float
    return parseFloat(value.replace("$", "").trim());
  };

  const handleParsedData = async (results: Papa.ParseResult<CSVModel>) => {
    if (results.errors.length > 0) {
      setError("Error parsing CSV file");
      return;
    }

    setLoading(true);
    try {
      for (const model of results.data) {
        // Validate required fields
        if (!model.vendor || !model.name || model.supported === undefined) {
          throw new Error(
            "Missing required fields (vendor, name, or supported)"
          );
        }

        const modelInput: CreateLLMModelInput = {
          id: model.id.toString(),
          vendor: model.vendor,
          status: model.status || "active",
          host: model.host,
          supported: model.supported.toLowerCase() === "true",
          name: model.name,
          description: model.description || null,
          modelKey: model.modelKey || null,
          modelKeyLatest: model.modelKeyLatest || null,
          inputTokenPrice: parseDollarAmount(model.inputTokenPrice),
          cachedTokenPrice: parseDollarAmount(model.cachedTokenPrice),
          outputTokenPrice: parseDollarAmount(model.outputTokenPrice),
          inputTokenBatchPrice: parseDollarAmount(model.inputTokenBatchPrice),
          outputTokenBatchPrice: parseDollarAmount(model.outputTokenBatchPrice),
          searchCost: model.searchCost
            ? parseDollarAmount(model.searchCost)
            : undefined,
          textInputSupport: model.textInputSupport.toLowerCase() === "true",
          imageInputSupport: model.imageInputSupport.toLowerCase() === "true",
          audioInputSupport: model.audioInputSupport.toLowerCase() === "true",
          textOutputSupport: model.textOutputSupport.toLowerCase() === "true",
          imageOutputSupport: model.imageOutputSupport.toLowerCase() === "true",
          audioOutputSupport: model.audioOutputSupport.toLowerCase() === "true",
          thinkingSupport: model.thinkingSupport.toLowerCase() === "true",
          contextWindowTokens: model.contextWindowTokens
            ? parseInt(model.contextWindowTokens)
            : null,
          maxResponseTokens: model.maxResponseTokens
            ? parseInt(model.maxResponseTokens)
            : null,
          knowledgeCutoffDate: model.knowledgeCutoffDate || null,
          lastCheckedDate: model.lastCheckedDate || null,
          supportedLanguages: model.supportedLanguages || null,
        };

        await API.graphql({
          query: createLLMModel,
          variables: { input: modelInput },
        });
      }
      setSuccess(`Successfully imported ${results.data.length} models`);
    } catch (err) {
      console.error("Error creating models:", err);
      setError(
        "Error creating models: " +
          (err instanceof Error ? err.message : String(err))
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Models Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        <Button variant="contained" component="label" disabled={loading}>
          Import Models CSV
          <input type="file" hidden accept=".csv" onChange={handleFileUpload} />
        </Button>

        <TextField
          label="Filter by Vendor"
          value={filterVendor}
          onChange={(e) => setFilterVendor(e.target.value)}
          size="small"
        />
      </Box>

      <Box sx={{ height: "100%", width: "100%" }}>
        <DataGrid
          rows={models.filter((model) =>
            model.vendor.toLowerCase().includes(filterVendor.toLowerCase())
          )}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          columns={columns}
          getRowHeight={() => "auto"}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
            sorting: {
              sortModel: [{ field: "outputTokenPrice", sort: "desc" }],
            },
            columns: {
              columnVisibilityModel: {
                id: false,
                vendor: true,
                host: false,
                supported: true,
                status: true,
                name: true,
                description: false,
                inputTokenPrice: true,
                outputTokenPrice: true,
                contextWindowTokens: true,
                maxResponseTokens: true,
                textInputSupport: false,
                imageInputSupport: false,
                audioInputSupport: false,
                textOutputSupport: false,
                imageOutputSupport: false,
                audioOutputSupport: false,
                thinkingSupport: false,
                knowledgeCutoffDate: false,
                lastCheckedDate: true,
                supportedLanguages: false,
              },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          //disableSelectionOnClick
          loading={loading}
          sx={{
            "& .MuiDataGrid-cell": {
              py: 1,
            },
          }}
        />
      </Box>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Model</DialogTitle>
        <DialogContent>
          {selectedModel && (
            <Box
              sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              {/* Basic Info */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Vendor"
                  value={selectedModel.vendor}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      vendor: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Host"
                  value={selectedModel.host}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      host: e.target.value,
                    })
                  }
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={selectedModel.status}
                    onChange={(e) =>
                      setSelectedModel({
                        ...selectedModel,
                        status: e.target.value,
                      })
                    }
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="beingDeprecated">
                      Being Deprecated
                    </MenuItem>
                    <MenuItem value="deprecated">Deprecated</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                    <MenuItem value="deleted">Deleted</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Name"
                  value={selectedModel.name}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      name: e.target.value,
                    })
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.supported}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          supported: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Supported"
                />
                <TextField
                  size="small"
                  label="Model Key"
                  value={selectedModel.modelKey || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      modelKey: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Latest Model Key"
                  value={selectedModel.modelKeyLatest || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      modelKeyLatest: e.target.value,
                    })
                  }
                />
              </Box>

              {/* Pricing */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Input Token Price"
                  type="number"
                  value={selectedModel.inputTokenPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      inputTokenPrice: parseFloat(e.target.value),
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Output Token Price"
                  type="number"
                  value={selectedModel.outputTokenPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      outputTokenPrice: parseFloat(e.target.value),
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Cached Token Price"
                  type="number"
                  value={selectedModel.cachedTokenPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      cachedTokenPrice: parseFloat(e.target.value),
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Input Batch Price"
                  type="number"
                  value={selectedModel.inputTokenBatchPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      inputTokenBatchPrice: parseFloat(e.target.value),
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Output Batch Price"
                  type="number"
                  value={selectedModel.outputTokenBatchPrice || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      outputTokenBatchPrice: parseFloat(e.target.value),
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Search Cost"
                  type="number"
                  value={selectedModel.searchCost || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      searchCost: parseFloat(e.target.value),
                    })
                  }
                />
              </Box>

              {/* Support Flags */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.textInputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          textInputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Text Input"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.imageInputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          imageInputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Image Input"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.audioInputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          audioInputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Audio Input"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.textOutputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          textOutputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Text Output"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.imageOutputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          imageOutputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Image Output"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.audioOutputSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          audioOutputSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Audio Output"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={selectedModel.thinkingSupport}
                      onChange={(e) =>
                        setSelectedModel({
                          ...selectedModel,
                          thinkingSupport: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Thinking"
                />
              </Box>

              {/* Other Fields */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Context Window Tokens"
                  type="number"
                  value={selectedModel.contextWindowTokens || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      contextWindowTokens: parseInt(e.target.value),
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Max Response Tokens"
                  type="number"
                  value={selectedModel.maxResponseTokens || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      maxResponseTokens: parseInt(e.target.value),
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Knowledge Cutoff Date"
                  value={selectedModel.knowledgeCutoffDate || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      knowledgeCutoffDate: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Last Checked Date"
                  value={selectedModel.lastCheckedDate || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      lastCheckedDate: e.target.value,
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Description"
                  multiline
                  rows={4}
                  value={selectedModel.description || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      description: e.target.value,
                    })
                  }
                  sx={{ gridColumn: "span 2" }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 2,
                }}
              >
                <TextField
                  size="small"
                  label="Supported Languages"
                  multiline
                  rows={2}
                  value={selectedModel.supportedLanguages || ""}
                  onChange={(e) =>
                    setSelectedModel({
                      ...selectedModel,
                      supportedLanguages: e.target.value,
                    })
                  }
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
